import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { PersonAttendance, ServiceStatus, ServiceType, WorshipService } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { addDays, formatDayHeading, formatTime, fromLocalInput, toLocalInput } from '../calendar/dates';
import { attendanceOpen, selfCheckInOpen } from '../attendance/window';
import { useMe } from '../session/SessionProvider';
import { ConflictsNotice } from '../events/Conflicts';
import { useShowRehearsals } from './RehearsalPages';

const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const STATUS: Record<ServiceStatus, { label: string; tone: string }> = {
  planned: { label: 'Programado', tone: 'badge--active' },
  held: { label: 'Realizado', tone: '' },
  canceled: { label: 'Cancelado', tone: 'badge--warn' },
};

/** Cultos de la Iglesia activa (Motor 7). La hora de cada culto vive en el Calendario. */
const PAST_DAYS = 180;

export function WorshipPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('worship.manage');
  const services = useAsync(
    (signal) => {
      if (!churchId) return Promise.resolve([]);
      // Seis meses atrás para «Cultos anteriores» y dos meses hacia adelante.
      const from = new Date(Date.now() - PAST_DAYS * 86400_000).toISOString();
      const to = new Date(Date.now() + 60 * 86400_000).toISOString();
      return api.get<{ items: WorshipService[] }>(`/worship/churches/${churchId}/services?from=${from}&to=${to}`, signal).then((r) => r.items);
    },
    [churchId],
  );
  const types = useAsync(
    (signal) =>
      churchId && canManage ? api.get<{ items: ServiceType[] }>(`/worship/churches/${churchId}/service-types`, signal).then((r) => r.items) : Promise.resolve([]),
    [churchId, canManage],
  );
  const canReadOwn = me.canOwn('attendance.read');
  const mine = useAsync(
    (signal) =>
      churchId && canReadOwn
        ? api.get<{ items: PersonAttendance[] }>(`/attendance/churches/${churchId}/people/${me.personId}`, signal).then((r) => new Set(r.items.map((i) => i.occasion.id)))
        : Promise.resolve(new Set<string>()),
    [churchId, me.personId],
  );
  const attended = mine.status === 'ready' ? mine.data : new Set<string>();
  const attendance = { staff: me.can('attendance.record'), self: me.canOwn('attendance.record') && canReadOwn };
  const canSeeTeam = me.can('assignments.read');
  const showRehearsals = useShowRehearsals(churchId);
  const [adding, setAdding] = useState(false);
  const [showPast, setShowPast] = useState(false);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  // Próximo: el primero que no ha terminado ni se canceló. Los ya realizados o terminados van a «anteriores».
  const now = new Date();
  const all = services.status === 'ready' ? services.data : [];
  const past = all.filter((s) => s.status === 'held' || new Date(s.endsAt) <= now);
  const coming = all.filter((s) => !past.includes(s));
  const next = coming.find((s) => s.status !== 'canceled') ?? null;
  const rest = coming.filter((s) => s !== next);

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <h1>Cultos</h1>
        <div className="actions actions--wrap">
          {showRehearsals ? (
            <Link className="btn btn--secondary" to="/app/ensayos">
              Ensayos
            </Link>
          ) : null}
          {canManage && !adding ? (
            <>
              <Link className="btn btn--secondary" to="/app/cultos/configurar">
                ⚙ Configurar cultos
              </Link>
              <Button onClick={() => setAdding(true)}>Nuevo culto</Button>
            </>
          ) : null}
        </div>
      </header>

      {adding && types.status === 'ready' ? (
        <NewServiceCard
          churchId={churchId}
          types={types.data}
          onDone={() => {
            setAdding(false);
            services.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      {services.status === 'loading' ? <Loading /> : null}
      {services.status === 'error' ? <ErrorState error={services.error} onRetry={services.reload} /> : null}
      {services.status === 'ready' && coming.length === 0 ? (
        <Card>
          <EmptyState title="No hay cultos programados.">
            {canManage ? <p className="muted">En «Configurar cultos» crea un tipo de culto con su día y hora y prográmalo para varias semanas.</p> : null}
          </EmptyState>
        </Card>
      ) : null}

      {next ? (
        <NextService
          churchId={churchId}
          service={next}
          attendance={attendance}
          attended={attended.has(next.id)}
          canSeeTeam={canSeeTeam}
          onCheckedIn={mine.reload}
        />
      ) : null}

      {rest.length > 0 ? (
        <Card title="Siguientes cultos">
          <ul className="list">
            {rest.map((s) => (
              <ServiceLine key={s.id} service={s} attended={attended.has(s.id)} />
            ))}
          </ul>
        </Card>
      ) : null}

      {past.length > 0 ? (
        <Card title={`Cultos anteriores (${past.length})`}>
          <ul className="list">
            {[...past].reverse().slice(0, showPast ? undefined : 3).map((s) => (
              <ServiceLine key={s.id} service={s} attended={attended.has(s.id)} />
            ))}
          </ul>
          {past.length > 3 ? (
            <Button variant="ghost" onClick={() => setShowPast((v) => !v)}>
              {showPast ? 'Ver solo los recientes' : `Ver todos (${past.length})`}
            </Button>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

/** El próximo culto, destacado, con lo que se usa ese día. */
function NextService({
  churchId,
  service: s,
  attendance,
  attended,
  canSeeTeam,
  onCheckedIn,
}: {
  churchId: string;
  service: WorshipService;
  attendance: { staff: boolean; self: boolean };
  attended: boolean;
  canSeeTeam: boolean;
  onCheckedIn: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function checkIn() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/attendance/churches/${churchId}/services/${s.id}/check-in`);
      onCheckedIn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Próximo culto">
      <div className="next-service">
        <span className="service__when">
          {formatDayHeading(new Date(s.startsAt))} · {formatTime(s.startsAt)}
        </span>
        <strong className="next-service__title">{s.title}</strong>
        {s.location ? <span className="muted small">{s.location}</span> : null}
        {s.notes ? <span className="muted small">{s.notes}</span> : null}
        {!attendance.staff && attendance.self && attended ? <span className="badge badge--active">Asististe</span> : null}
      </div>
      <div className="actions actions--wrap">
        {!attendance.staff && attendance.self && !attended && selfCheckInOpen(s) ? (
          <Button busy={busy} onClick={() => void checkIn()}>
            Estoy aquí
          </Button>
        ) : null}
        <Link className="btn btn--primary" to={`/app/cultos/${s.id}`}>
          Abrir culto
        </Link>
        {canSeeTeam ? (
          <Link className="btn btn--secondary" to={`/app/cultos/${s.id}/equipo`}>
            Equipo
          </Link>
        ) : null}
        {attendance.staff && attendanceOpen(s) ? (
          <Link className="btn btn--secondary" to={`/app/cultos/${s.id}/asistencia`}>
            Asistencia
          </Link>
        ) : null}
      </div>
      {error ? <Alert>{error}</Alert> : null}
    </Card>
  );
}

/** Una línea por culto: tocarla abre el culto (ahí están Cantos, Equipo, Asistencia y las demás opciones). */
function ServiceLine({ service: s, attended }: { service: WorshipService; attended: boolean }) {
  const label = s.status === 'planned' ? null : STATUS[s.status];
  return (
    <li className="list__item">
      <Link className="list__link list__link--row service-line" to={`/app/cultos/${s.id}`}>
        <span className="service-line__when">
          {formatDayHeading(new Date(s.startsAt))} · {formatTime(s.startsAt)}
        </span>
        <strong className="service-line__title">{s.title}</strong>
        {label ? <span className={`badge ${label.tone}`}>{label.label}</span> : null}
        {attended ? <span className="badge badge--active">Asististe</span> : null}
      </Link>
    </li>
  );
}

/** Lo que se configura una vez: tipos de culto (con su día y hora) y el orden del culto. */
export function ServiceSettingsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('worship.manage');
  const types = useAsync(
    (signal) => (churchId ? api.get<{ items: ServiceType[] }>(`/worship/churches/${churchId}/service-types`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  const [adding, setAdding] = useState(false);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (!canManage) return <EmptyState title="Solo la administración configura los cultos." />;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/cultos">← Cultos</Link>
      </p>
      <header className="page__header">
        <h1>Configurar cultos</h1>
      </header>

      <Card title="Tipos de culto" action={!adding ? <Button variant="secondary" onClick={() => setAdding(true)}>+ Nuevo tipo</Button> : null}>
        <p className="small muted">Cada tipo tiene su día y hora habitual. «Programar» crea sus cultos en el calendario por las semanas que elijas.</p>
        {types.status === 'loading' ? <Loading /> : null}
        {types.status === 'error' ? <ErrorState error={types.error} onRetry={types.reload} /> : null}
        {types.status === 'ready' ? (
          <>
            {types.data.length === 0 && !adding ? <EmptyState title="Aún no hay tipos de culto." /> : null}
            <ul className="list">
              {types.data.map((t) => (
                <ServiceTypeRow key={t.id} churchId={churchId} type={t} canManage onChanged={types.reload} />
              ))}
            </ul>
            {adding ? (
              <TypeForm
                churchId={churchId}
                onSaved={() => {
                  setAdding(false);
                  types.reload();
                }}
                onCancel={() => setAdding(false)}
              />
            ) : null}
          </>
        ) : null}
      </Card>

      <Card title="Orden del culto">
        <p className="small muted">Las partes del culto en orden (dirige, alabanza, ofrendas, predicación, anuncios…) y quién las da.</p>
        <Link className="btn btn--secondary" to="/app/cultos/orden">
          Editar el orden del culto
        </Link>
      </Card>

      <TestimonySettings churchId={churchId} />
    </div>
  );
}

/** ¿Los testimonios y agradecimientos que pide la iglesia se aprueban antes de entrar al orden? */
function TestimonySettings({ churchId }: { churchId: string }) {
  const settings = useAsync((signal) => api.get<{ testimonyApproval: boolean }>(`/worship/churches/${churchId}/order`, signal), [churchId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (settings.status !== 'ready') return null;
  async function toggle(on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/worship/churches/${churchId}/worship-settings`, { testimonyApproval: on });
      settings.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Testimonios y agradecimientos">
      <p className="small muted">Cualquier miembro puede pedir dar un testimonio o agradecimiento en un culto (desde el Orden del culto). También queda en Testimonios.</p>
      <label className="check">
        <input type="checkbox" checked={settings.data.testimonyApproval} disabled={busy} onChange={(e) => void toggle(e.target.checked)} /> Quien dirige el culto (o la administración) los aprueba antes de que entren al orden
      </label>
      {error ? <Alert>{error}</Alert> : null}
    </Card>
  );
}

const WEEK_OPTIONS = [4, 8, 12, 26];
const WEEKDAYS_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];

/** Un tipo de culto: programarlo varias semanas, editarlo o borrarlo (se archiva; sus cultos se quedan). */
function ServiceTypeRow({ churchId, type: t, canManage, onChanged }: { churchId: string; type: ServiceType; canManage: boolean; onChanged: () => void }) {
  const [mode, setMode] = useState<'view' | 'schedule' | 'edit'>('view');
  const [weeks, setWeeks] = useState(4);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function schedule() {
    setBusy(true);
    setMessage(null);
    try {
      const { created, skipped } = await api.post<{ created: unknown[]; skipped: number }>(`/worship/churches/${churchId}/service-types/${t.id}/schedule`, { weeks });
      const text = created.length === 0 ? `Las próximas ${weeks} semanas ya estaban programadas.` : `Se programaron ${created.length} cultos${skipped ? ` (${skipped} ya existían)` : ''}.`;
      setMessage({ tone: 'success', text });
      setMode('view');
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`¿Borrar el tipo «${t.name}»? Ya no se podrá programar. Los cultos que ya tiene (hechos o programados) no se borran.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.delete(`/worship/churches/${churchId}/service-types/${t.id}`);
      onChanged();
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
      setBusy(false);
    }
  }

  const habitual = t.weekday !== null && t.startTime ? `${WEEKDAYS[t.weekday]} ${t.startTime}` : 'Sin día fijo';
  if (mode === 'edit') {
    return (
      <li className="list__item">
        <TypeForm
          churchId={churchId}
          type={t}
          onSaved={() => {
            setMode('view');
            onChanged();
          }}
          onCancel={() => setMode('view')}
        />
      </li>
    );
  }
  return (
    <li className="list__item service">
      <div className="service__main">
        <strong>{t.name}</strong>
        <span className="muted small">
          {habitual} · {t.durationMinutes} min{t.defaultLocation ? ` · ${t.defaultLocation}` : ''}
        </span>
      </div>
      {canManage ? (
        <div className="actions actions--wrap">
          {t.weekday !== null && mode === 'view' ? (
            <Button variant="secondary" onClick={() => setMode('schedule')}>
              Programar
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setMode('edit')}>
            Editar
          </Button>
          <Button variant="ghost" busy={busy && mode === 'view'} onClick={() => void remove()}>
            Borrar
          </Button>
        </div>
      ) : null}
      {mode === 'schedule' && t.weekday !== null ? (
        <div className="form">
          <div className="field">
            <label htmlFor={`weeks-${t.id}`}>¿Cuántas semanas?</label>
            <select id={`weeks-${t.id}`} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
              {WEEK_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} semanas
                </option>
              ))}
            </select>
          </div>
          <p className="small">
            Todos los {WEEKDAYS_PLURAL[t.weekday]} a las {t.startTime}, {weeks} semanas.
          </p>
          <div className="actions">
            <Button busy={busy} onClick={() => void schedule()}>
              Programar {weeks} semanas
            </Button>
            <Button variant="ghost" onClick={() => setMode('view')}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
      {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
    </li>
  );
}

/** Crear un tipo de culto o editar uno (lo editado vale para los cultos que se programen después). */
function TypeForm({ churchId, type, onSaved, onCancel }: { churchId: string; type?: ServiceType; onSaved: () => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const p = type ? `type-${type.id}` : 'type-new';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const weekday = String(form.get('weekday'));
    const startTime = String(form.get('startTime') || '');
    const body = {
      name: String(form.get('name')),
      weekday: weekday === '' ? null : Number(weekday),
      startTime: weekday === '' ? null : startTime || null,
      durationMinutes: Number(form.get('durationMinutes') || 120),
      defaultLocation: String(form.get('defaultLocation') || '') || null,
    };
    setBusy(true);
    setError(null);
    try {
      if (type) await api.put(`/worship/churches/${churchId}/service-types/${type.id}`, body);
      else await api.post(`/worship/churches/${churchId}/service-types`, body);
      formEl.reset();
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <h3>{type ? `Editar «${type.name}»` : 'Nuevo tipo de culto'}</h3>
      {type ? <p className="small muted">Los cambios valen para los cultos que se programen de aquí en adelante; los ya programados no cambian.</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      <Field label="Nombre" name="name" required minLength={2} maxLength={120} placeholder="Culto dominical" defaultValue={type?.name ?? ''} />
      <div className="form__row">
        <div className="field">
          <label htmlFor={`${p}-weekday`}>Día habitual</label>
          <select id={`${p}-weekday`} name="weekday" defaultValue={type ? (type.weekday === null ? '' : String(type.weekday)) : '0'}>
            <option value="">Sin día fijo</option>
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <Field label="Hora" name="startTime" type="time" defaultValue={type ? (type.startTime ?? '') : '10:00'} />
      </div>
      <div className="form__row">
        <Field label="Duración (minutos)" name="durationMinutes" type="number" min={5} max={1440} defaultValue={type?.durationMinutes ?? 120} />
        <Field label="Lugar habitual" name="defaultLocation" maxLength={200} defaultValue={type?.defaultLocation ?? ''} />
      </div>
      <div className="actions">
        <Button type="submit" busy={busy}>
          {type ? 'Guardar cambios' : 'Crear tipo'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function NewServiceCard({ churchId, types, onDone, onCancel }: { churchId: string; types: ServiceType[]; onDone: () => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const defaultStart = addDays(new Date(), 1);
  defaultStart.setHours(19, 0, 0, 0);
  const [slot, setSlot] = useState(() => ({ startsAt: toLocalInput(defaultStart), endsAt: toLocalInput(new Date(defaultStart.getTime() + 120 * 60_000)), place: '' }));

  function onFormChange(event: FormEvent<HTMLFormElement>) {
    const f = new FormData(event.currentTarget);
    const starts = String(f.get('startsAt') ?? '');
    const type = types.find((t) => t.id === f.get('serviceTypeId'));
    const minutes = Number(f.get('durationMinutes')) || type?.durationMinutes || 120;
    const start = new Date(starts);
    const next = {
      startsAt: starts,
      endsAt: Number.isNaN(start.getTime()) ? '' : toLocalInput(new Date(start.getTime() + minutes * 60_000)),
      place: String(f.get('location') ?? '') || type?.defaultLocation || '',
    };
    if (next.startsAt !== slot.startsAt || next.endsAt !== slot.endsAt || next.place !== slot.place) setSlot(next);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const serviceTypeId = String(form.get('serviceTypeId') || '') || null;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/worship/churches/${churchId}/services`, {
        serviceTypeId,
        title: String(form.get('title') || '') || null,
        location: String(form.get('location') || '') || null,
        notes: String(form.get('notes') || '') || null,
        startsAt: fromLocalInput(String(form.get('startsAt'))),
        durationMinutes: form.get('durationMinutes') ? Number(form.get('durationMinutes')) : null,
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Nuevo culto">
      <form className="form" onSubmit={onSubmit} onChange={onFormChange} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <div className="field">
          <label htmlFor="serviceTypeId">Tipo</label>
          <select id="serviceTypeId" name="serviceTypeId" defaultValue="">
            <option value="">Culto especial (sin tipo)</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <Field label="Nombre" name="title" maxLength={150} hint="Opcional si eliges un tipo." />
        <div className="form__row">
          <Field label="Fecha y hora" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(defaultStart)} />
          <Field label="Duración (minutos)" name="durationMinutes" type="number" min={5} max={1440} placeholder="120" />
        </div>
        <Field label="Lugar" name="location" maxLength={200} />
        <ConflictsNotice churchId={churchId} startsAt={slot.startsAt} endsAt={slot.endsAt} place={slot.place} />
        <Field label="Notas" name="notes" maxLength={2000} />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Programar culto
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
